import { useCallback } from "react";
import {
  getInspectionResults,
  getInspectionResultsFullHierarchy,
} from "../../../api/InspectionResults";
import { getInspectionDefFullHierarchy } from "../../../api/InspectionDefinitions";
import { useWorkOrderStore } from "../../../store/wo-order";

type InspectionContextOptions = {
  isEquipmentContext?: boolean;
  equipmentId?: string;
};

export default function useGetInspection() {
  const setWoInspectionData = useWorkOrderStore(
    (state) => state.setWoInspectionData
  );

  const getInspectionDefHierarchy = useCallback(
    async (
      inspectionId: number | string,
      woNumber: string,
      options?: { isEquipmentContext?: boolean }
    ) => {
      const defResponse = await getInspectionDefFullHierarchy({
        // explicitly convert to string -- iOS issue
        inspectionId: String(inspectionId),
        includeWoDetails: !options?.isEquipmentContext,
        includeEqDetails: !!options?.isEquipmentContext,
      });
      if (!options?.isEquipmentContext && woNumber) {
      setWoInspectionData(woNumber, {
        isCompleted: false,
        inspectionDef: defResponse.data,
        inspectionId: String(inspectionId),
      });
    }
      return defResponse;
    },
    [setWoInspectionData]
  );

  const getInspection = useCallback(
    async (
      woNumber: string,
      inspectionId: string,
      options?: InspectionContextOptions
    ) => {
      let hasError = false;
      let data: any = undefined;
      let isCompleted = false;

      const isEquipmentContext =
        !!options?.isEquipmentContext && !!options?.equipmentId;

      const buildFilter = (): string => {
        const safeInspectionId = String(inspectionId);
        if (isEquipmentContext) {
          const equipmentId = String(options?.equipmentId ?? "");
          return `equipment='${equipmentId}' AND inspection_id='${safeInspectionId}'`;
        }
        const woBase = woNumber.includes("-")
          ? woNumber.split("-")[0]
          : woNumber;
        return `wo_base='${woBase}' AND inspection_id='${safeInspectionId}'`;
      };

      let response = await getInspectionResults(buildFilter());
      hasError = response.isError;
      let status = response.status;
      data = response.data;
      if (!response.isError) {
        if (response.data?.length > 0) {
          const matchingResult = response.data.find((result: any) => {
            if (isEquipmentContext) {
              const apiEquipment = result.Equipment || "";
              return (
                apiEquipment === String(options?.equipmentId ?? "")
              );
            }

            let apiWorkOrder = result.WoBase || "";

            if (result.WoTask && result.WoTask.trim() !== "") {
              apiWorkOrder += `-${result.WoTask}`;
            }

            if (result.WoSubtask && result.WoSubtask.trim() !== "") {
              apiWorkOrder += `-${result.WoSubtask}`;
            }

            return apiWorkOrder === woNumber;
          });
          if (matchingResult) {
            const { ResultId, InspectionId } = matchingResult;
            const resultResponse = await getInspectionResultsFullHierarchy({
              //explicitly convert to string -- iOS issue
              inspectionId: String(InspectionId),
              resultId: String(ResultId),
            });
            hasError = resultResponse.isError;
            status = resultResponse.status;
            data = resultResponse.data;
            const inspectionResult = resultResponse.data?.InspectionResult;
            isCompleted = !!(
              inspectionResult?.ResultId && inspectionResult?.InspectedDatetime
            );
            // Cache only for work-order-based inspections
            if (!isEquipmentContext && woNumber) {
              setWoInspectionData(woNumber, {
                isCompleted,
                inspectionResults: resultResponse.data,
                inspectionId: InspectionId,
              });
            }
          }
        } else {
          const defResponse = await getInspectionDefHierarchy(
            inspectionId,
            woNumber,
            { isEquipmentContext }
          );

          hasError = defResponse.isError;
          status = defResponse.status;
          data = defResponse.data;
        }
      }
      return { hasError, data, status, isCompleted };
    },
    [getInspectionDefHierarchy, setWoInspectionData]
  );

  return { getInspection, getInspectionDefHierarchy };
}
